export type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

/** Methods safe to replay: no side effect if the first attempt did land. */
const IDEMPOTENT_METHODS = new Set<HttpMethod>(['GET', 'HEAD', 'DELETE']);

export const MAX_BACKOFF_MS = 8_000;

/**
 * Decide whether to retry.
 *
 * Non-idempotent methods retry **only** on 429: a throttled request was never
 * processed, so replaying it cannot duplicate an order. Any other failure on a
 * POST could mean "the order was created but the response was lost", so we
 * surface it instead of risking a double charge.
 */
export function shouldRetry(params: {
  method: HttpMethod;
  status?: number;
  isConnectionError?: boolean;
  attempt: number;
  maxRetries: number;
}): boolean {
  const { method, status, isConnectionError, attempt, maxRetries } = params;
  if (attempt >= maxRetries) return false;

  const idempotent = IDEMPOTENT_METHODS.has(method);

  if (isConnectionError === true) return idempotent;
  if (status === undefined) return false;
  if (status === 429) return true;
  if (!RETRYABLE_STATUSES.has(status)) return false;

  return idempotent;
}

/**
 * Parse a `Retry-After` header. Supports both delta-seconds and an HTTP-date.
 * Returns seconds, or undefined when absent/unparseable.
 */
export function parseRetryAfter(
  headerValue: string | null | undefined,
  now: number = Date.now(),
): number | undefined {
  if (headerValue === null || headerValue === undefined) return undefined;
  const trimmed = headerValue.trim();
  if (trimmed === '') return undefined;

  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed);
  }

  const asDate = Date.parse(trimmed);
  if (Number.isNaN(asDate)) return undefined;
  return Math.max(0, Math.ceil((asDate - now) / 1000));
}

/**
 * Full-jitter exponential backoff: `random(0, min(base * 2^attempt, cap))`.
 * A `Retry-After` hint always wins.
 */
export function backoffDelayMs(params: {
  attempt: number;
  baseDelayMs: number;
  retryAfterSeconds?: number | undefined;
  random?: () => number;
}): number {
  const { attempt, baseDelayMs, retryAfterSeconds, random = Math.random } = params;

  if (retryAfterSeconds !== undefined) {
    return Math.max(0, retryAfterSeconds * 1000);
  }

  const ceiling = Math.min(baseDelayMs * Math.pow(2, attempt), MAX_BACKOFF_MS);
  return Math.floor(random() * ceiling);
}
